import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import LogoutIcon from '@mui/icons-material/Logout';
import {
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import routeIcon from '@/shared/configs/routeIcon';
import { AppPath } from '@/shared/constants/path';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import NavigationItem from '@/shared/types/navigationItem.type';

const navigationConfigTop: NavigationItem[] = [
  {
    label: 'customers',
    href: AppPath.Customers,
  },
  {
    label: 'orders',
    href: AppPath.Orders,
  },
  {
    label: 'products',
    href: AppPath.Products,
  },
  {
    label: 'inventory',
    href: AppPath.Inventory,
  },
  {
    label: 'bookings',
    href: AppPath.Bookings,
  },
  {
    label: 'payments',
    href: AppPath.Payments,
  },
  {
    label: 'checkout',
    href: AppPath.Checkout,
  },
  {
    label: 'shipping',
    href: AppPath.Shipping,
  },
];

const navigationConfigBottom: NavigationItem[] = [
  {
    label: 'settings',
    href: AppPath.Settings,
  },
];

const NavList: FC = () => {
  const { t } = useTranslation(['common']);
  const { logout } = useAuth();
  const redirectLogin = useRedirectLogin();

  const handleSignOut = () => {
    logout();
    redirectLogin();
  };

  return (
    <>
      <List component='nav' sx={{ height: '100%', width: '100%' }}>
        {navigationConfigTop.map(({ href, label }) => {
          const regex = new RegExp(`(^|/)${href}(/|$)`);
          const selected = regex.test(window.location.pathname);

          return (
            <ListItemButton
              key={label}
              component={Link}
              to={href}
              selected={selected}
              sx={{
                'borderRadius': 4,
                'py': 1.5,
                '&.Mui-selected': {
                  color: selected ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              <ListItemIcon>
                {routeIcon[href]?.renderIcon({
                  width: 24,
                  height: 24,
                  color: selected ? 'primary.main' : 'text.secondary',
                })}
              </ListItemIcon>

              <Typography component='div' noWrap>
                {t(label)}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>

      <List component='nav' sx={{ mt: 'auto' }}>
        {navigationConfigBottom.map(({ href, label }) => {
          const selected = window.location.pathname.includes(href);

          return (
            <ListItemButton
              key={label}
              component={Link}
              to={href}
              selected={selected}
              sx={{
                'borderRadius': 4,
                'py': 1.5,
                '&.Mui-selected': {
                  color: selected ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              <ListItemIcon>
                {routeIcon[href]?.renderIcon({
                  width: 24,
                  height: 24,
                  color: selected ? 'primary.main' : 'text.secondary',
                })}
              </ListItemIcon>
              <Typography component='div' noWrap>
                {t(label)}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>

      <Stack>
        <ListItemButton sx={{ borderRadius: 4 }} onClick={handleSignOut}>
          <ListItemAvatar>
            <LogoutIcon
              sx={{
                color: 'text.secondary',
                width: 20,
                height: 20,
              }}
            />
          </ListItemAvatar>
          <Typography component='div' noWrap>
            {t('signout')}
          </Typography>
        </ListItemButton>
      </Stack>
    </>
  );
};

export default NavList;
